import {
  Alert,
  AlertActions,
  AlertContent,
  Button
} from '@deque/cauldron-react';
import React, { useState } from 'react';
import { Toast } from '@deque/cauldron-react';
import analyticsInstances, {
  getFallbackAnalyticsInstanceId
} from '../../../../common/analyticsInstances';
import { type IntegrationProductInfo } from '../../../../common/utils/get-integration-product-name-from-slug';
import styles from './ConnectionButtonsLine.css';
import { type AuthUser } from '../../../../common/contexts/auth';
import { type v2 } from '@deque/billing-service-client';
import { type IntegrationConnection } from '../../../../common/utils/integrations-client/jira/connections';
import { useGlobalToast } from '../../../../common/contexts/globalToast';
import ScrimmedLoader from '../../../../common/components/ScrimmedLoader';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { type getActionsFn } from '../../../utils/getJiraOnPremActions';

interface ConnectionButtonsLineProps {
  connection: IntegrationConnection | null;
  integrationProductInfo: IntegrationProductInfo;
  enterprise: v2.Enterprise;
  canManageIntegrations: boolean;
  authUser: AuthUser;
  connectionsUpdated: () => void;
  getActions: getActionsFn;
}

export function ConnectionButtonsLine({
  connection,
  integrationProductInfo,
  enterprise,
  canManageIntegrations,
  authUser,
  connectionsUpdated,
  getActions
}: ConnectionButtonsLineProps) {
  const { t } = useTranslation();

  const history = useHistory();

  const analytics =
    analyticsInstances[
      getFallbackAnalyticsInstanceId(
        integrationProductInfo.integrationProductSlug
      )
    ];
  const { setContents } = useGlobalToast();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const { displayName } = integrationProductInfo;

  const actions = getActions({
    displayName,
    integrationProductInfo,
    connection,
    authUser,
    enterprise,
    analytics,
    setIsProcessing,
    setError,
    connectionsUpdated,
    setContents,
    t
  });

  if (isProcessing) {
    return <ScrimmedLoader label={t('Loading...')} />;
  }

  let errorToast = null;
  if (error) {
    errorToast = (
      <Toast show type="error" onDismiss={() => setError('')}>
        {error}
      </Toast>
    );
  }

  if (connection) {
    const isConnected = connection.status === 'connected';

    return (
      <>
        {showAlert && (
          <Alert
            heading={t('Delete connection')}
            onClose={() => setShowAlert(false)}
            show={showAlert}
            variant="warning"
          >
            <AlertContent>
              {t(
                'Deleting this connection will also delete all templates for this connection. Are you sure you want to delete this connection?'
              )}
            </AlertContent>
            <AlertActions>
              <Button variant="error" onClick={actions.deleteConnection}>
                {t('Delete')}
              </Button>
              <Button variant="secondary" onClick={() => setShowAlert(false)}>
                {t('Cancel')}
              </Button>
            </AlertActions>
          </Alert>
        )}
        <div className={styles.installedGroup}>
          {errorToast}
          {!isConnected && canManageIntegrations && (
            <Button
              className={styles.darkText}
              variant="secondary"
              onClick={actions.connect}
              aria-label={t('Connect to {{displayName}} {{baseUrl}}', {
                displayName,
                baseUrl: connection.base_url
              })}
            >
              {t('Connect to integration')}
            </Button>
          )}
          {isConnected && (
            <Button
              className={styles.darkText}
              variant="secondary"
              onClick={() => {
                history.push(
                  `/configuration/integrations/${connection.id}/templates`
                );
              }}
              aria-label={t(
                'Configure {{displayName}} templates for {{baseUrl}}',
                {
                  displayName,
                  baseUrl: connection.base_url
                }
              )}
            >
              {t('Configure templates')}
            </Button>
          )}
          {isConnected && canManageIntegrations && (
            <Button
              variant="secondary"
              onClick={actions.disconnect}
              aria-label={t('Disconnect {{displayName}} from {{baseUrl}}', {
                displayName,
                baseUrl: connection.base_url
              })}
            >
              {t('Disconnect')}
            </Button>
          )}
          {!isConnected && canManageIntegrations && (
            <Button
              variant="secondary"
              onClick={() => setShowAlert(true)}
              aria-label={t('Delete {{displayName}} {{baseUrl}} connection', {
                displayName,
                baseUrl: connection.base_url
              })}
            >
              {t('Delete connection')}
            </Button>
          )}
        </div>
      </>
    );
  } else {
    if (canManageIntegrations) {
      return (
        <div className={styles.installedGroup}>
          {errorToast}
          <Button
            variant="primary"
            onClick={actions.connect}
            aria-label={t('Add new connection')}
          >
            {t('Add new connection to {{displayName}}', { displayName })}
          </Button>
        </div>
      );
    }
  }

  return null;
}
